// The Analytics page, and each campaign's Results tab: what the videos posted
// on the brand's campaigns did over a range of days (shared/brand-api.ts
// BrandPerformance). The five numbers on top are also the chart's switch, so
// the chart shows one measure on one axis, with the days before dashed behind
// it. Then what the videos cost, the videos by views gained (each plays here,
// in TikTok's own player), and the same numbers by campaign, creator and the
// country TikTok says a video went up from. Videos handed over as files have
// no post we can read, so they are only counted.

import {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import type {BrandCampaign, BrandPerformance, PerformanceGroup, PerformanceNumbers, PerformanceRange, PerformanceVideo} from '../../shared/brand-api';
import {countryName} from '../../shared/countries';
import {TrendChart, formatNumber} from '../admin/components/Charts';
import type {TrendSeries} from '../admin/components/Charts';
import {Icon} from '../creator/components/Icon';
import {Person} from '../shared/person/Person';
import {creatorPicture, describe, fetchPerformance} from './api';
import {useCreatorProfile} from './creator-profile';
import {InfoTip} from './info-tip';
import {usd} from './plan-credit';
import {useBrand} from './store';
import './performance.css';

const RANGES: [PerformanceRange, string, string][] = [
  ['7d', '7 days', 'The 7 days before'],
  ['30d', '30 days', 'The 30 days before'],
  ['90d', '90 days', 'The 90 days before'],
  ['all', 'All time', ''],
];

type Metric = 'views' | 'engagement' | 'likes' | 'comments' | 'shares';
const METRICS: [Metric, string, string][] = [
  ['views', 'Views', 'eye'],
  ['engagement', 'Engagement', 'trend'],
  ['likes', 'Likes', 'heart'],
  ['comments', 'Comments', 'chat'],
  ['shares', 'Shares', 'share'],
];

const TIERS: Record<string, string> = {'1k': '1K', '10k': '10K', '100k': '100K', '250k': '250K', '500k': '500K', '750k': '750K', '1m': '1M'};
const regions = (() => {
  try { return new Intl.DisplayNames(['en'], {type: 'region'}); } catch { return null; }
})();
const country = (code: string) => (code ? regions?.of(code) ?? countryName(code) : 'Not known yet');
const started = (campaign: BrandCampaign) => campaign.status === 'active' || campaign.status === 'paused' || campaign.status === 'completed';

/** Likes, comments and shares per view, as a percentage; null without views. */
function engagement(numbers: PerformanceNumbers): number | null {
  return numbers.views > 0 ? ((numbers.likes + numbers.comments + numbers.shares) / numbers.views) * 100 : null;
}

function percent(value: number | null): string {
  if (value === null) return '–';
  return `${value >= 10 || Number.isInteger(value) ? Math.round(value) : value.toFixed(1)}%`;
}

function metricValue(metric: Metric, numbers: PerformanceNumbers): number | null {
  return metric === 'engagement' ? engagement(numbers) : numbers[metric];
}

/** Against the days before: a count as a percentage, the engagement rate in points. */
function change(metric: Metric, now: PerformanceNumbers, before: PerformanceNumbers | null): {text: string; tone: string} | null {
  if (!before) return null;
  const a = metricValue(metric, now);
  const b = metricValue(metric, before);
  if (a === null || b === null || (metric !== 'engagement' && b === 0)) return null;
  const moved = metric === 'engagement' ? Math.round((a - b) * 10) / 10 : Math.round(((a - b) / b) * 100);
  const sign = moved > 0 ? '+' : moved < 0 ? '−' : '';
  return {text: `${sign}${Math.abs(moved)}${metric === 'engagement' ? ' pts' : '%'}`, tone: moved > 0 ? ' up' : moved < 0 ? ' down' : ''};
}

function updatedAgo(at: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(at)) / 60_000));
  if (minutes < 2) return 'Updated just now';
  if (minutes < 60) return `Updated ${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Updated ${hours} h ago`;
  const days = Math.round(hours / 24);
  return `Updated ${days} ${days === 1 ? 'day' : 'days'} ago`;
}

/** In UTC, as the days of a range are, so a video posted near midnight shows the day it counts on. */
function postedOn(video: PerformanceVideo): string {
  return video.posted_at ? new Date(video.posted_at).toLocaleDateString(undefined, {day: 'numeric', month: 'short', timeZone: 'UTC'}) : 'Posted';
}

/** Only a video link plays; a photo post's id has no player and no cover. */
const playable = (video: PerformanceVideo) => Boolean(video.video_id) && /\/video\/\d/.test(video.url);
const handle = (video: PerformanceVideo) => (video.creator_handle ? `@${video.creator_handle}` : video.creator_name);

/**
 * The report for every campaign (`campaignId` undefined: the Analytics page,
 * with a campaign picker) or one (its Results tab). The range and the picked
 * campaign live in the address, so a reload or a shared link keeps them.
 */
export function Performance({campaignId}: {campaignId?: string}) {
  const {data} = useBrand();
  const [params, setParams] = useSearchParams();
  const range = RANGES.find(([key]) => key === params.get('range'))?.[0] ?? '30d';
  const campaigns = (data?.campaigns ?? []).filter(started);
  const picked = campaignId ?? (campaigns.some((item) => item.id === params.get('campaign')) ? params.get('campaign') : null);
  // A campaign's Results before it went live: nothing to ask for yet.
  const waiting = Boolean(campaignId) && !campaigns.some((item) => item.id === campaignId);
  const scope = picked ? campaigns.filter((item) => item.id === picked) : campaigns;
  const filesOnly = scope.length > 0 && scope.every((item) => item.delivery_mode === 'file_only');
  const [report, setReport] = useState<BrandPerformance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [metric, setMetric] = useState<Metric>('views');
  const [allVideos, setAllVideos] = useState(false);
  const [watching, setWatching] = useState<PerformanceVideo | null>(null);

  useEffect(() => {
    if (waiting) return undefined;
    let live = true;
    setLoading(true);
    setError('');
    fetchPerformance(range, picked)
      .then((value) => { if (live) { setReport(value); setAllVideos(false); } })
      // The last report belonged to the range or campaign before: not shown under this one.
      .catch((caught) => { if (live) { setReport(null); setError(describe(caught)); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [range, picked, waiting]);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, {replace: true});
  }

  if (waiting) {
    return (
      <section className="card brand-section">
        <div className="empty-state"><Icon name="chart" size={28} /><b>No results yet</b><span>Views show here once the campaign is live and creators post.</span></div>
      </section>
    );
  }

  const before = RANGES.find(([key]) => key === range)![2];
  const label = METRICS.find(([key]) => key === metric)![1];
  const series: TrendSeries[] = [{
    key: metric,
    label,
    color: 'var(--chart-1)',
    // A day without views has no engagement rate, rather than a rate of nothing.
    points: report ? report.days.filter((day) => metric !== 'engagement' || day.views > 0)
      .map((day) => ({day: day.day, value: metricValue(metric, day) ?? 0})) : [],
  }];
  // The days before, behind it; not for the engagement rate, whose quiet days have none.
  if (report?.previous_days && metric !== 'engagement') {
    series.push({
      key: 'previous', label: before, color: 'var(--chart-muted)', dashed: true,
      points: report.previous_days.map((day) => ({day: day.day, value: metricValue(metric, day) ?? 0})),
    });
  }
  const empty = report !== null && report.videos.length === 0 && report.totals.views === 0;

  return (
    <div className="performance">
      <div className="perf-controls">
        {campaignId ? null : (
          <label className="brand-field perf-campaign">
            <span className="sr-only">Campaign</span>
            <select value={picked ?? ''} onChange={(event) => setParam('campaign', event.target.value || null)}>
              <option value="">All campaigns</option>
              {campaigns.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
            </select>
          </label>
        )}
        <div className="perf-range">
          {report?.updated_at && !empty ? (
            <span className="perf-updated">{updatedAgo(report.updated_at)}<InfoTip text="Numbers come from TikTok: every hour in a video's first two days, then daily to day 30." /></span>
          ) : null}
          <div className="filter-tabs" role="tablist" aria-label="Days">
            {RANGES.map(([key, name]) => (
              <button key={key} type="button" role="tab" aria-selected={range === key} className={range === key ? 'active' : ''}
                onClick={() => setParam('range', key === '30d' ? null : key)}>{name}</button>
            ))}
          </div>
        </div>
      </div>
      {error ? <p className="form-alert">{error}</p> : null}
      {report === null && !error ? <section className="card brand-section"><div className="empty-state"><span className="loader" /></div></section> : null}
      {report ? (
        <div className={loading ? 'perf-body refreshing' : 'perf-body'}>
          {empty
            ? <NoVideos report={report} filesOnly={filesOnly} onAllTime={() => setParam('range', 'all')} />
            : <>
              <section className="card brand-section perf-main">
                <div className="perf-metrics" role="tablist" aria-label="Show on the chart">
                  {METRICS.map(([key, name, icon]) => {
                    const value = metricValue(key, report.totals);
                    const moved = change(key, report.totals, report.previous);
                    return (
                      <button key={key} type="button" role="tab" aria-selected={metric === key} className="perf-metric" onClick={() => setMetric(key)}>
                        <small><Icon name={icon} size={15} />{name}</small>
                        <b>{key === 'engagement' ? percent(value) : formatNumber(value ?? 0)}</b>
                        {moved ? <span className={`perf-change${moved.tone}`} title={`Against ${before.toLowerCase()}`}>{moved.text}</span> : <span className="perf-change" />}
                      </button>
                    );
                  })}
                </div>
                <TrendChart height={260} format={metric === 'engagement' ? percent : formatNumber} empty={<span>Nothing new in these days</span>} series={series} />
              </section>
              <CostStrip report={report} />
              <VideoList videos={report.videos} listed={report.videos_listed} showCampaign={!picked} all={allVideos} onShowAll={() => setAllVideos(true)} onOpen={setWatching} />
              <Breakdown report={report} byCampaign={!picked} />
            </>}
        </div>
      ) : null}
      {watching ? <VideoPeek video={watching} onClose={() => setWatching(null)} /> : null}
    </div>
  );
}

function NoVideos({report, filesOnly, onAllTime}: {report: BrandPerformance; filesOnly: boolean; onAllTime(): void}) {
  const files = report.files_delivered
    ? `${report.files_delivered} ${report.files_delivered === 1 ? 'video was' : 'videos were'} handed over as files${report.range === 'all' ? '' : ' in these days'}.`
    : '';
  if (report.videos_total === 0 && filesOnly) {
    return (
      <section className="card brand-section">
        <div className="empty-state"><Icon name="file" size={28} /><b>Videos come to you as files</b>
          <span>You post them yourself, so there are no TikTok numbers here. {files}</span></div>
      </section>
    );
  }
  return (
    <section className="card brand-section">
      <div className="empty-state">
        <Icon name="chart" size={28} />
        {report.videos_total === 0 ? <>
          <b>No posted videos yet</b>
          <span>Numbers show here once creators post on your campaign accounts. {files}</span>
        </> : <>
          <b>Nothing in these days</b>
          <span>No video was posted or gained views in this range. {files}</span>
          <button className="text-button" type="button" onClick={onAllTime}>See all time</button>
        </>}
      </div>
    </section>
  );
}

function CostStrip({report}: {report: BrandPerformance}) {
  const listed = report.videos_listed;
  return (
    <div className="stat-strip">
      <div className="stat-tile">
        <small>Videos posted</small>
        <b>{report.totals.videos_posted}</b>
        {report.files_delivered
          ? <span>{report.files_delivered} handed over as files <InfoTip text="Videos handed over as files go up where you post them, so we have no numbers for them." /></span>
          : <span>&nbsp;</span>}
      </div>
      <div className="stat-tile">
        <small>Views per video</small>
        <b>{listed ? formatNumber(Math.round(report.totals.views / listed)) : '–'}</b>
        <span>{listed} {listed === 1 ? 'video' : 'videos'} in these days <InfoTip text="The views gained in these days, divided by the videos that were posted or gained views in them." /></span>
      </div>
      {report.cost ? <>
        <div className="stat-tile">
          <small>Cost per 1K views</small>
          <b>{report.cost.per_1k_views_cents === null ? '–' : usd(report.cost.per_1k_views_cents)}</b>
          <span>So far <InfoTip text="What the videos posted in these days cost you, video and view bonuses, per 1,000 of the views they have had so far. It falls as they keep getting views." /></span>
        </div>
        <div className="stat-tile">
          <small>Cost per video</small>
          <b>{report.cost.per_video_cents === null ? '–' : usd(report.cost.per_video_cents)}</b>
          <span>Average <InfoTip text="What each video approved in these days cost you on average, with its view bonuses so far. Files handed over count too." /></span>
        </div>
      </> : null}
    </div>
  );
}

const SHOWN = 10;

function VideoList({videos, listed, showCampaign, all, onShowAll, onOpen}: {
  videos: PerformanceVideo[]; listed: number; showCampaign: boolean; all: boolean; onShowAll(): void; onOpen(video: PerformanceVideo): void;
}) {
  const shown = all ? videos : videos.slice(0, SHOWN);
  return (
    <section className="card brand-section">
      <div className="section-head"><div><h2>Videos</h2><p>Most views in these days first{listed > videos.length ? `, the top ${videos.length} of ${listed}` : ''}</p></div></div>
      {videos.length ? (
        <div className="perf-videos">
          {shown.map((video) => <VideoRow key={video.deliverable_id} video={video} showCampaign={showCampaign} onOpen={onOpen} />)}
        </div>
      ) : <div className="empty-state"><Icon name="video" size={26} /><b>No videos in these days</b></div>}
      {!all && videos.length > SHOWN ? <button className="text-button perf-more" type="button" onClick={onShowAll}>Show all {videos.length}</button> : null}
    </section>
  );
}

function Cover({video, width}: {video: PerformanceVideo; width: 128 | 480}) {
  const [failed, setFailed] = useState(false);
  return playable(video) && !failed
    ? <img src={`/api/brand/tiktok-cover/${video.video_id}?w=${width}`} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
    : <Icon name="tiktok" size={18} />;
}

function VideoRow({video, showCampaign, onOpen}: {video: PerformanceVideo; showCampaign: boolean; onOpen(video: PerformanceVideo): void}) {
  const line = [
    showCampaign ? video.campaign_title : '',
    postedOn(video),
    video.posted_from ? `posted from ${country(video.posted_from)}` : '',
  ].filter(Boolean).join(' · ');
  const total = video.views > video.gained.views ? `${formatNumber(video.views)} in all` : '';
  return (
    <button type="button" className="perf-video" onClick={() => onOpen(video)} aria-label={`Watch ${handle(video)}’s video`}>
      <span className="perf-cover"><Cover video={video} width={128} />{playable(video) ? <i className="perf-play"><Icon name="play" size={10} /></i> : null}</span>
      <span className="perf-who"><b>{handle(video)}</b><small>{line}</small></span>
      <span className="perf-num"><b>{video.captured_at ? formatNumber(video.gained.views) : '–'}</b><small>{video.captured_at ? total || 'views' : 'no numbers yet'}</small></span>
      <span className="perf-num perf-num-rate"><b>{percent(engagement(video.gained))}</b><small>engagement</small></span>
      {video.status === 'checking_link' ? <span className="status-pill in_review">Link being checked</span>
        : video.being_checked ? <span className="status-pill in_review">Being checked</span>
          : video.highest_tier ? <span className="status-pill active">{TIERS[video.highest_tier]}</span> : <span className="perf-pill-spacer" />}
    </button>
  );
}

/** One video, played here in TikTok's own player, with its numbers so far. */
function VideoPeek({video, onClose}: {video: PerformanceVideo; onClose(): void}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const numbers: [string, string][] = [
    ['Views', video.captured_at ? formatNumber(video.views) : '–'],
    ['Engagement', percent(engagement(video))],
    ['Likes', formatNumber(video.likes)],
    ['Comments', formatNumber(video.comments)],
    ['Shares', formatNumber(video.shares)],
  ];
  return (
    <div className="switch-layer">
      <div className="switch-backdrop" onClick={onClose} />
      <div className="card switch-dialog perf-peek" role="dialog" aria-modal="true" aria-labelledby="peek-title">
        <button className="switch-close" type="button" onClick={onClose} aria-label="Close"><Icon name="x" size={18} /></button>
        <div className="perf-peek-player">
          {playable(video)
            ? <iframe src={`https://www.tiktok.com/player/v1/${video.video_id}?autoplay=1&rel=0`} title="The video on TikTok" allow="autoplay; encrypted-media; fullscreen; picture-in-picture" />
            : <Cover video={video} width={480} />}
        </div>
        <div className="perf-peek-facts">
          <p className="eyebrow">{video.campaign_title}</p>
          <h2 id="peek-title">{handle(video)}</h2>
          <p className="perf-peek-line">{[postedOn(video), video.posted_from ? `posted from ${country(video.posted_from)}` : ''].filter(Boolean).join(' · ')}</p>
          <dl className="perf-peek-numbers">
            {numbers.map(([name, value]) => <div key={name}><dt>{name}</dt><dd>{value}</dd></div>)}
          </dl>
          {video.being_checked ? <p className="perf-peek-note"><Icon name="info" size={15} />The views are being checked; these are the ones we trust so far.</p> : null}
          <a className="btn btn-light" href={video.url} target="_blank" rel="noopener">Open on TikTok<Icon name="external" size={15} /></a>
        </div>
      </div>
    </div>
  );
}

/** A creator as the portal shows one everywhere: picture, name and handle, opening their profile. */
function CreatorCell({group, videos, onOpen}: {group: PerformanceGroup; videos: PerformanceVideo[]; onOpen(): void}) {
  const video = videos.find((one) => one.account_id === group.key);
  return video
    ? <Person name={video.creator_name} handle={video.creator_handle} sources={creatorPicture(video)} size="sm" onOpen={onOpen} />
    : <Person name={group.label} size="sm" onOpen={onOpen} />;
}

type Dimension = 'campaigns' | 'creators' | 'countries';

function Breakdown({report, byCampaign}: {report: BrandPerformance; byCampaign: boolean}) {
  const dimensions: [Dimension, string][] = [
    ...(byCampaign ? [['campaigns', 'Campaigns'] as [Dimension, string]] : []),
    ['creators', 'Creators'],
    ['countries', 'Countries'],
  ];
  const [chosen, setChosen] = useState<Dimension>(dimensions[0]![0]);
  const dimension = dimensions.some(([key]) => key === chosen) ? chosen : dimensions[0]![0];
  const profile = useCreatorProfile();
  const rows = report[dimension];
  const cost = report.cost !== null;
  const name = (group: PerformanceGroup) => (dimension === 'countries' ? country(group.key) : group.label);
  return (
    <section className="card brand-section">
      <div className="section-head perf-breakdown-head">
        <h2>Breakdown</h2>
        <div className="filter-tabs" role="tablist" aria-label="Break down by">
          {dimensions.map(([key, text]) => (
            <button key={key} type="button" role="tab" aria-selected={dimension === key} className={dimension === key ? 'active' : ''} onClick={() => setChosen(key)}>{text}</button>
          ))}
        </div>
      </div>
      <table className="perf-table">
        <thead><tr>
          <th>{dimension === 'campaigns' ? 'Campaign' : dimension === 'creators' ? 'Creator' : <>Posted from <InfoTip text="The country TikTok recorded each video was uploaded from. TikTok first shows a new video to people in that country." /></>}</th>
          <th>Videos</th><th>Views</th><th className="perf-col-rate">Engagement</th>
          {cost ? <th className="perf-col-cost">Cost per 1K views</th> : null}
        </tr></thead>
        <tbody>{rows.map((group) => (
          <tr key={group.key || 'unknown'}>
            <td>{dimension === 'creators' ? <CreatorCell group={group} videos={report.videos} onOpen={() => profile.open(group.key)} /> : name(group)}</td>
            <td>{group.videos}</td>
            <td>{formatNumber(group.gained.views)}</td>
            <td className="perf-col-rate">{percent(engagement(group.gained))}</td>
            {cost ? <td className="perf-col-cost">{group.cost_per_1k_views_cents === null ? '–' : usd(group.cost_per_1k_views_cents)}</td> : null}
          </tr>
        ))}</tbody>
      </table>
      {profile.panel}
    </section>
  );
}
